// Fixed consent-callback.ts
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FiDataService } from '../../core/services/fi-data.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-consent-callback',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './consent-callback.html',
  styleUrls: ['./consent-callback.scss'], // Fixed: Use styleUrls array
})
export class ConsentCallback implements OnInit, OnDestroy { // Added OnDestroy
  isProcessing = true;
  isSuccess = false;
  isError = false;
  isPendingVerification = false;
  title = 'Processing Consent...';
  message = 'Please wait while we process your consent request.';
  callbackParams: any = null; // TODO: Define interface
  statusClass = 'processing';

  // To store data between retries (no change)
  consentHandle: string | null = null;
  mobileNumber: string | null = null;
  productId: string | null = null;

  private subscriptions: Subscription = new Subscription(); // For cleanup

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fiDataService: FiDataService
  ) { }

  ngOnInit(): void {
    // const existingId = localStorage.getItem('active_consent_id');
    // if (existingId) {
    //   this.router.navigate(['/dashboard']);
    //   return;
    // }

    this.processCallback();
  }

  private processCallback(): void {
    const sub = this.route.queryParams.pipe(take(1)).subscribe(params => { // take(1) for auto-unsubscribe
      this.callbackParams = params;

      // Attempt to recover mobile/product from storage immediately (use sessionStorage for temp data?)
      this.mobileNumber = localStorage.getItem('consent_mobile_number');
      this.productId = localStorage.getItem('consent_product_id');

      if (params['consent_handle'] && params['transaction_id']) {
        this.statusClass = 'processing';
        this.prepareVerification(params['consent_handle']);
      } else if (params['ecres'] || (params['fi'] && params['resdate'])) {
        this.handleRecoveryOrEncrypted();
      } else if (params['error']) {
        this.router.navigate(['/request-consent']);
      } else {
        if (this.mobileNumber) {
          this.handleRecoveryOrEncrypted();
        } else {
          this.router.navigate(['/request-consent']);
        }
      }
    });

    this.subscriptions.add(sub);
  }

  private handleRecoveryOrEncrypted(): void {
    this.statusClass = 'processing';
    this.title = 'Finalizing Consent...';
    this.message = 'Bank response received. Verifying status...';

    if (this.mobileNumber) {
      this.checkConsentStatus();
    } else {
      this.router.navigate(['/request-consent']);
    }
  }

  private prepareVerification(rawHandle: string): void {
    this.consentHandle = rawHandle;
    if (typeof this.consentHandle === 'string' && this.consentHandle.startsWith('[')) {
      try {
        const parsed = JSON.parse(this.consentHandle);
        this.consentHandle = Array.isArray(parsed) ? parsed[0] : this.consentHandle;
      } catch (e) {
        // Silent fail, but log in dev
      }
    }

    if (!this.mobileNumber && this.callbackParams.vua) {
      const match = this.callbackParams.vua.match(/^(\d{10})@/);
      if (match) this.mobileNumber = match[1];
    }

    this.checkConsentStatus();
  }

  checkConsentStatus(): void {
    if (!this.mobileNumber) {
      console.warn('Consent search aborted: Missing mobile number');
      this.setErrorState('Missing Information', 'Cannot verify consent: Missing mobile number.');
      return;
    }

    this.isProcessing = true;
    this.isPendingVerification = false;
    this.isError = false;
    this.isSuccess = false;
    this.statusClass = 'processing';
    this.title = 'Verifying Consent Status...';
    this.message = 'Checking if your account linking is active...';

    const accountId = localStorage.getItem('consent_account_id') || undefined;

    this.fiDataService.getConsentsList({
      mobileNumber: this.mobileNumber,
      productID: this.productId!
    }).pipe(take(1)).subscribe({
      next: (response) => {
        this.isProcessing = false;

        if (response.status === 'success' && Array.isArray(response.data)) {
          const allConsents = [...response.data];

          // Robust sort: Most recent first
          allConsents.sort((a: any, b: any) => {
            const dateB = new Date(b.consentCreationDate || b.consentCreationData || 0).getTime();
            const dateA = new Date(a.consentCreationDate || a.consentCreationData || 0).getTime();
            return dateB - dateA;
          });

          let matchedConsent: any = null;
          const storedHandle = localStorage.getItem('active_consent_handle');

          // 1. Try to match currently processed handle if present in URL
          if (this.consentHandle) {
            matchedConsent = allConsents.find(c => c.consentHandle === this.consentHandle);
          }

          // 2. If no direct handle or no match, look for ACTIVE consents in the list
          if (!matchedConsent) {
            matchedConsent = allConsents.find(c => c.status === 'ACTIVE');
          }

          // 3. Fallback to stored handle or most recent
          if (!matchedConsent) {
            if (storedHandle) {
              matchedConsent = allConsents.find(c => c.consentHandle === storedHandle);
            }
            if (!matchedConsent) {
              matchedConsent = allConsents[0];
            }
          }

          if (matchedConsent) {
            const status = (matchedConsent.status || 'UNKNOWN').toUpperCase();

            if (status === 'ACTIVE') {
              const consentId = matchedConsent.consentID || matchedConsent.consentId;
              if (consentId) {
                localStorage.setItem('active_consent_id', consentId);
                this.isSuccess = true;
                this.statusClass = 'success';
                this.title = 'Consent Active!';
                this.message = 'Verification successful. Fetching your financial data...';
                this.fetchFIData(consentId);
              } else {
                // Technically active but missing ID? Should not happen but handle it.
                this.setRetryState('Pending ID', 'Consent is active but account association is still finalizing. Please wait a moment.');
              }
            } else {
              this.isPendingVerification = false;
              this.statusClass = 'warning';
              this.title = 'Approval Pending';
              this.message = `Your consent is currently ${status}. Please ensure you have approved the request in your AA app and then click "Check Verification Status" below.`;
            }
          } else {
            this.setRetryState('No Record Found', 'We could not find any consent entries for your number. Please try starting over.');
          }
        } else {
          this.setRetryState('Verification Incomplete', response.message || 'The server returned an unsuccessful status.');
        }
      },
      error: (err) => {
        this.isProcessing = false;
        console.error('Consent List Error:', err);
        this.setRetryState('Network Error', 'Could not reach the verification server. Please check your connection and try again.');
      }
    });
  }

  private fetchFIData(consentId: string): void {
    const sub = this.fiDataService.getAllFIData(consentId, 'new').pipe(take(1)).subscribe({
      next: (response) => {
        this.router.navigate(['/dashboard']);
      },
      error: (error) => {
        this.title = 'Consent Active';
        this.message = 'Consent is active, but we could not auto-refresh data immediately. Redirecting...';
        setTimeout(() => this.router.navigate(['/dashboard']), 2000);
      }
    });

    this.subscriptions.add(sub);
  }

  private setRetryState(title: string, message: string): void {
    this.isProcessing = false;
    this.isPendingVerification = true;
    this.statusClass = 'warning';
    this.title = title;
    this.message = message;
  }

  private setErrorState(title: string, message: string): void {
    this.isProcessing = false;
    this.isError = true;
    this.statusClass = 'error';
    this.title = title;
    this.message = message;
  }

  goBack(): void {
    this.router.navigate(['/request-consent']);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe(); // Prevent memory leaks
  }
}